import { zooplaTheme } from '@blueprint/theme';
import triggerTracking from '@blueprint/tracking';
import { ThemeProvider } from '@blueprint/ui';
import { SearchPageType, SearchSource } from '@zoopla/listing-search-context';
import {
    getBedsMax,
    getBedsMin,
    getDuration,
    getIsRetirementHome,
    getPriceFrequency,
    getPriceMax,
    getPriceMin,
    getPropertySubType,
    getPropertyType,
    getQ,
    getRadius,
    getSearchPath,
    getTransportType
} from '@zoopla/listing-search/filters/modal';
import { LocationSearchInput } from '@zoopla/listing-search/input';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';
import { formatLocation } from '../utils/formatLocation';
import { formatSearchPath } from '../utils/formatSearchPath';
import { getUpdatedQuery } from '../utils/getUpdatedQuery';
import {
    BedroomsWrapper,
    LocationWrapper,
    PriceRangeMultiColumnDropdownWrapper,
    PropertyTypeWrapper,
    SearchInitationWrapper,
    SearchRadiusWrapper,
    StyledButton,
    StyledButtonWrapper,
    StyledDivider,
    StyledDividerInner,
    TravelTimeWrapper
} from './SearchInitiation.styles';
import { BedsMax, BedsMin, SelectBeds } from './SelectBeds';
import {
    Duration,
    SelectMaxTravelTimeAndTransportMode,
    TransportType
} from './SelectMaxTravelTimeAndTransportMode';
import { SelectNewHomesDevelopersPropertyType } from './SelectNewHomesDevelopersPropertyType';
import { PriceMax, PriceMin, SelectPriceForSale } from './SelectPriceForSale';
import { PriceFrequency, SelectPriceToRent } from './SelectPriceToRent';
import {
    PropertySubType,
    PropertyType,
    SelectPropertyType
} from './SelectPropertyType';
import { Radius, SelectRadius } from './SelectRadius';

type Tab = SearchPageType | 'house-prices';

const SEARCH_BUTTON_TEXT = 'Search';

export type SearchInitiationProps = {
    searchType: Tab;
    showRadius?: boolean;
    eventCategory: 'homepage' | 'search results' | 'new_homes';
    searchSource: SearchSource;
};

export const SearchInitiation: FC<
    React.PropsWithChildren<SearchInitiationProps>
> = ({ searchType, showRadius = true, eventCategory, searchSource }) => {
    const { query, push, pathname } = useRouter();

    const [formValue, updateFormValue] = useState({
        beds_max: getBedsMax(query),
        beds_min: getBedsMin(query),
        duration: getDuration(query),
        is_retirement_home: getIsRetirementHome(query),
        price_frequency: getPriceFrequency(query),
        price_max: getPriceMax(query),
        price_min: getPriceMin(query),
        property_sub_type: getPropertySubType(query),
        property_type: getPropertyType(query),
        q: getQ(query),
        radius: getRadius(query),
        transport_type: getTransportType(query)
    });

    const searchPath = getSearchPath(query);

    const isTravelTimeSearch = Boolean(
        getDuration(query) && getTransportType(query)
    );

    const formattedSearchPath = formatSearchPath(searchPath);

    const [locationText, setLocationText] = useState(
        formValue.q || formattedSearchPath
    );
    const [identifier, setIdentifier] = useState(formatLocation(searchPath));

    const [hasSubmittedLocationSearch, setHasSubmittedLocationSearch] =
        useState(false);

    const isForSaleSearch =
        searchType === 'for-sale' || searchType === 'new-homes';
    const isNewHomesDevelopersSearch = searchSource === 'new-homes-dev';

    const viewType = pathname.includes('/map/') ? 'map' : 'list';

    const handleSearchProperties = async () => {
        if (!locationText) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        const eventLabel = locationText
            ? `${searchType}-success`
            : `${searchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const updatedQuery = getUpdatedQuery({
            query,
            formValue,
            isNewHomesDevelopersSearch,
            identifier,
            searchSource,
            viewType
        });

        await push({
            pathname: pathname,
            query: {
                ...updatedQuery
            }
        });
    };

    const renderRadiusAndTravelTime = () => {
        if (!isTravelTimeSearch) {
            if (showRadius) {
                return (
                    <>
                        <StyledDivider>
                            <StyledDividerInner />
                        </StyledDivider>
                        <SearchRadiusWrapper>
                            <SelectRadius
                                value={formValue.radius as Radius}
                                onChange={(radius) => {
                                    updateFormValue((oldFormValue) => ({
                                        ...oldFormValue,
                                        radius: radius
                                    }));
                                }}
                            />
                        </SearchRadiusWrapper>
                    </>
                );
            }

            return null;
        }

        return (
            <ThemeProvider theme={zooplaTheme}>
                <TravelTimeWrapper>
                    <SelectMaxTravelTimeAndTransportMode
                        value={{
                            duration: formValue.duration as Duration,
                            transportType:
                                formValue.transport_type as TransportType
                        }}
                        onChange={({ duration, transportType }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                duration: duration,
                                transport_type: transportType
                            }));
                        }}
                    />
                </TravelTimeWrapper>
            </ThemeProvider>
        );
    };

    const showError = hasSubmittedLocationSearch && !locationText;

    return (
        <ThemeProvider theme={zooplaTheme}>
            <SearchInitationWrapper>
                <LocationWrapper>
                    <LocationSearchInput
                        id="header-location"
                        onLocationInputKeyDown={handleSearchProperties}
                        hasExtendedHeight
                        hasDarkPurpleBorder
                        hasBorderRight={false}
                        value={locationText as string}
                        showError={showError}
                        onlyDisplayRemoveIconWhenActive={false}
                        onLocationSelect={({ identifier, q }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                q: q
                            }));
                            setIdentifier(identifier);
                        }}
                        handleInputTextChange={(value) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                q: ''
                            }));
                            setIdentifier(value);
                            setLocationText(value);
                        }}
                    />
                </LocationWrapper>
                {renderRadiusAndTravelTime()}

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <BedroomsWrapper>
                    <SelectBeds
                        value={{
                            bedsMin: formValue.beds_min as BedsMin,
                            bedsMax: formValue.beds_max as BedsMax
                        }}
                        onChange={({ bedsMin, bedsMax }) => {
                            updateFormValue((oldFormValue) => ({
                                ...oldFormValue,
                                beds_min: bedsMin,
                                beds_max: bedsMax
                            }));
                        }}
                    />
                </BedroomsWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PriceRangeMultiColumnDropdownWrapper>
                    {isForSaleSearch ? (
                        <SelectPriceForSale
                            value={{
                                priceMin: formValue.price_min as PriceMin,
                                priceMax: formValue.price_max as PriceMax
                            }}
                            onChange={({ priceMin, priceMax }) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    price_min: priceMin,
                                    price_max: priceMax
                                }));
                            }}
                        />
                    ) : (
                        <SelectPriceToRent
                            value={{
                                priceMin: formValue.price_min as PriceMin,
                                priceMax: formValue.price_max as PriceMax,
                                priceFrequency: (formValue.price_frequency ||
                                    'per_month') as PriceFrequency
                            }}
                            onChange={({
                                priceMin,
                                priceMax,
                                priceFrequency
                            }) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    price_min: priceMin,
                                    price_max: priceMax,
                                    price_frequency: priceFrequency
                                }));
                            }}
                        />
                    )}
                </PriceRangeMultiColumnDropdownWrapper>

                <StyledDivider>
                    <StyledDividerInner />
                </StyledDivider>

                <PropertyTypeWrapper>
                    {isNewHomesDevelopersSearch ? (
                        <SelectNewHomesDevelopersPropertyType
                            value={formValue.property_type as PropertyType}
                            onChange={(propertyType) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    property_type: propertyType
                                }));
                            }}
                        />
                    ) : (
                        <SelectPropertyType
                            value={{
                                propertyType:
                                    formValue.property_type as PropertyType,
                                propertySubType:
                                    formValue.property_sub_type as PropertySubType
                            }}
                            onChange={({ propertySubType, propertyType }) => {
                                updateFormValue((oldFormValue) => ({
                                    ...oldFormValue,
                                    property_sub_type: propertySubType,
                                    property_type: propertyType
                                }));
                            }}
                        />
                    )}
                </PropertyTypeWrapper>

                <StyledButtonWrapper>
                    <StyledButton
                        type="button"
                        onClick={handleSearchProperties}
                        data-testid="search-button"
                    >
                        {SEARCH_BUTTON_TEXT}
                    </StyledButton>
                </StyledButtonWrapper>
            </SearchInitationWrapper>
        </ThemeProvider>
    );
};
